// Lib
import React, { ComponentType } from 'react';
import { connect } from 'react-redux';
import { hasChanged } from '../../utils/react/propsComparisons';

// Util
import { createStructuredSelector } from 'reselect';

const currentBoardIdChanged = hasChanged('currentBoardId');

interface BreadcrumbObj {
    path: string[];
}

interface OwnProps {
    currentBoardId: string;
    breadcrumbIds: string[];
    breadcrumbObj: BreadcrumbObj;
}

const getBreadcrumbIds = (state: object, ownProps: OwnProps): string[] => {
    return ownProps.breadcrumbObj.path || [];
};

interface DesiredSelection {
    breadcrumbIds: string[];
}

const mapStateToProps = createStructuredSelector<object, OwnProps, DesiredSelection>({
    breadcrumbIds: getBreadcrumbIds,
});

const connector = connect(mapStateToProps);

export default (scrollPositions: Map<string, number> = new Map<string, number>()) =>
    (DecoratedComponent: ComponentType<any>): ComponentType<any> => {
        class MobileScrollPositionRetainDecorator extends React.Component<OwnProps> {
            componentDidUpdate(prevProps: OwnProps) {
                if (currentBoardIdChanged(prevProps, this.props)) {
                    this.restoreScrollPosition(this.props);
                }
            }

            getSnapshotBeforeUpdate(prevProps: OwnProps, prevState: object) {
                if (currentBoardIdChanged(prevProps, this.props)) {
                    const isInPrevBreadcrumbIds = (boardId: string) => prevProps.breadcrumbIds.includes(boardId);
                    const isInCurrentBreadcrumbIds = (boardId: string) => this.props.breadcrumbIds.includes(boardId);

                    if (prevProps.breadcrumbIds.every(isInCurrentBreadcrumbIds)) {
                        // If the current breadcrumb is a subset of the previous one, then this is a forward navigation
                        this.saveScrollPosition(prevProps);
                    } else if (this.props.breadcrumbIds.every(isInPrevBreadcrumbIds)) {
                        // If the previous breadcrumb is a subset of the current one, then this is a back navigation
                        scrollPositions.delete(prevProps.currentBoardId);
                    } else {
                        // If it's not a forward or back navigation, then it's a direct URL change.
                        scrollPositions.clear();
                    }
                }
            }

            saveScrollPosition = (props: OwnProps) => {
                const { currentBoardId } = props;

                // If it's a fresh page load, no need to save return location
                if (!currentBoardId) return;

                const scrollTop = document.scrollingElement?.scrollTop ?? 0;

                const isDefaultReturnState = scrollTop === 0;

                if (isDefaultReturnState) {
                    scrollPositions.delete(currentBoardId);
                    return;
                }

                scrollPositions.set(currentBoardId, scrollTop);
            };

            restoreScrollPosition = (props: OwnProps) => {
                const { currentBoardId } = props;

                const scrollPositionForBoardId = scrollPositions.get(currentBoardId) ?? 0;

                document.scrollingElement?.scrollTo(0, scrollPositionForBoardId);
            };

            render() {
                return <DecoratedComponent {...this.props} />;
            }
        }

        return connector(MobileScrollPositionRetainDecorator);
    };
